import { useEffect, useState } from 'react';

import { useDebounce } from './use-debounce';

/**
 * Hook for managing input values with immediate UI updates and debounced store updates.
 *
 * @param value - The current value from the store
 * @param onUpdate - Callback to update the store (will be debounced)
 * @param delay - Debounce delay in milliseconds (default: 500ms)
 * @returns A tuple of [localValue, setLocalValue] for use in controlled inputs
 *
 * @example
 * ```tsx
 * const [title, setTitle] = useDebouncedInput(
 *   page?.title || '',
 *   (value) => { setPage({ ...page, title: value }); },
 *   500
 * );
 * <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
 *
 * const [rotation, setRotation] = useDebouncedInput(
 *   totalRotation,
 *   (total) => { setFineRotation(total - baseRotation); saveToHistory(); },
 *   300
 * );
 * <Slider value={[rotation]} onValueChange={(v) => setRotation(v[0])} />
 * ```
 */
export function useDebouncedInput<T>(
  value: T,
  onUpdate: (value: T) => void,
  delay = 500,
): [T, (value: T) => void] {
  const [localValue, setLocalValue] = useState<T>(value);
  const debouncedUpdate = useDebounce(onUpdate, delay);

  // Sync local state when store value changes (e.g., from external updates)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: T) => {
    setLocalValue(newValue);
    debouncedUpdate(newValue);
  };

  return [localValue, handleChange];
}
